import { Confetti } from "@/components/molecules/confetti";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/features/utils/i18n";
import { useLocation, useNavigate } from "react-router-dom";
import { HeyUpTeamContact } from "./heyup-team-contact";

/** What the wizard hands the dashboard when the client says "C'est tout bon". */
export type WelcomeState = { agentName?: string };

/** Navigation state that opens the welcome over the dashboard. */
export const welcomeNavigationState = (welcome: WelcomeState) => ({ welcome });

/**
 * "Bravo, bienvenue chez HeyUp" — the end of onboarding A, over the dashboard.
 *
 * Deliberately almost empty. Everything that is left to configure after the
 * wizard (a number, tools, services, the team) is real, but listed here it
 * read as a wall of chores a minute after the client heard their agent for the
 * first time. So the one thing offered is a person: the HeyUp team, on the
 * phone, who walk them through the rest (`HeyUpTeamContact`, which the
 * sidebar's "Premiers pas" offers again). The confetti go off once, as it opens;
 * closing it clears the navigation state so neither comes back.
 */
export function WelcomeDialog() {
  const { t } = useTranslation("onboarding");
  const location = useLocation();
  const navigate = useNavigate();

  const welcome = (location.state as { welcome?: WelcomeState } | null)
    ?.welcome;
  if (!welcome) return null;

  const close = () =>
    navigate(location.pathname, { replace: true, state: null });
  const name = welcome.agentName?.trim();

  return (
    <>
      <Confetti />
      <Dialog open onOpenChange={(open) => !open && close()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="items-center text-center">
            <p className="text-4xl" aria-hidden>
              🎉
            </p>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              {t("welcome.title")}
            </DialogTitle>
            <DialogDescription>
              {name
                ? t("welcome.subtitleNamed", { name })
                : t("welcome.subtitle")}
            </DialogDescription>
          </DialogHeader>

          <HeyUpTeamContact />

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={close}
          >
            {t("welcome.close")}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
